import { qsa } from '../core/dom.js';

// ============================================================================
// Form module — native Constraint Validation API, no dependencies.
// Replaces the legacy jQuery + Parsley implementation with the same UX:
// per-field error lists, recovered state, server-error injection,
// focus-first-error, AJAX submit with min duration, mailto fallback,
// success view, and CustomEvents for analytics adapters.
//
// Binds every form[data-form]. Configuration via data attributes on the form:
//   data-endpoint       AJAX URL. Without it the form submits natively
//                       (validation still runs and can block submission).
//   data-action         appended as `action` (WP admin-ajax pattern)
//   data-nonce          appended as `_ajax_nonce`
//   data-hp-token       honeypot token appended as `_hp_token`
//   data-contact-email  enables the client-built mailto fallback
//   data-phone-display  phone number used in error copy
//   data-form-name      reported in the form:success event (analytics)
//   data-demo           "1" = no network; simulate success (for the gallery)
//   data-form-testing   "1" = autofill bar + server-side debug logging on submit
//
// Field extras:
//   data-format="phone-us"  live (XXX) XXX-XXXX mask
//   data-min-digits="10"    minimum digit count (phone validation)
//   data-error-*            per-field message overrides:
//     data-error-required / data-error-email / data-error-pattern / data-error-digits
//
// Related markup (all optional):
//   [data-form-alert]    form-level error region (.form-alert)
//   [data-form-success]  success overlay shown after delivery (.form-success-overlay)
//   [data-form-success-dismiss]  closes the overlay and reveals the form beneath
//   button[type=submit] with .btn__label / .btn__loading spans for the
//   loading swap (.btn.is-loading).
//
// Events dispatched on the form (bubbling):
//   form:success  detail: { data }        delivered
//   form:failure  detail: { message }     delivery failed (after fallbacks)
//   form:invalid  detail: { fields }      blocked by validation
// ============================================================================

const SUBMIT_MIN_DURATION_MS = 2000;
const CONTACT_SENT_COOKIE = 'lpr_contact_sent';
const CONTACT_SENT_MAX_AGE_SEC = 365 * 24 * 60 * 60;
const SNAPSHOT_ACTION = 'sky_form_snapshot';

const DEFAULT_MESSAGES = {
	required: 'This field is required.',
	email: 'Please enter a valid email address.',
	pattern: 'Please match the requested format.',
	digits: 'Please enter a valid phone number.',
	generic: 'Please correct this field.',
};

export function initForms() {
	qsa('form[data-form]').forEach(setupForm);
}

// --- Field-level validation --------------------------------------------------

function formatUSPhone(value) {
	const digits = value.replace(/\D/g, '').slice(0, 10);
	if (digits.length === 0) return '';
	if (digits.length <= 3) return `(${digits}`;
	if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
	return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function validatableFields(form) {
	return qsa('input, select, textarea', form).filter(
		(el) => el.name && el.type !== 'hidden' && el.type !== 'submit' && !el.disabled
	);
}

// Apply custom rules (min-digits) through setCustomValidity, then let the
// native API judge. Returns the message to show, or '' when valid.
function fieldMessage(input) {
	input.setCustomValidity('');

	const minDigits = Number(input.dataset.minDigits);
	if (minDigits && input.value.trim() !== '') {
		const digits = input.value.replace(/\D/g, '').length;
		if (digits < minDigits) {
			input.setCustomValidity(input.dataset.errorDigits || DEFAULT_MESSAGES.digits);
		}
	}

	if (input.checkValidity()) return '';

	const v = input.validity;
	if (v.customError) return input.validationMessage;
	if (v.valueMissing) return input.dataset.errorRequired || DEFAULT_MESSAGES.required;
	if (v.typeMismatch && input.type === 'email') return input.dataset.errorEmail || DEFAULT_MESSAGES.email;
	if (v.tooShort) {
		return input.dataset.errorMinlength || `Please enter at least ${input.minLength} characters.`;
	}
	if (v.patternMismatch) return input.dataset.errorPattern || DEFAULT_MESSAGES.pattern;
	return input.validationMessage || DEFAULT_MESSAGES.generic;
}

function errorListFor(input, create = false) {
	const id = `${input.id || input.name}-errors`;
	let list = document.getElementById(id);

	if (!list && create) {
		list = document.createElement('ul');
		list.className = 'field-errors';
		list.id = id;
		input.insertAdjacentElement('afterend', list);
		input.setAttribute('aria-describedby', id);
	}

	return list;
}

function showFieldError(input, message) {
	const list = errorListFor(input, true);
	list.replaceChildren();
	const item = document.createElement('li');
	item.textContent = message;
	list.appendChild(item);

	input.classList.add('is-invalid');
	input.classList.remove('is-recovered');
	input.setAttribute('aria-invalid', 'true');
}

function clearFieldError(input, { recovered = false } = {}) {
	errorListFor(input)?.replaceChildren();
	input.classList.remove('is-invalid');
	input.classList.toggle('is-recovered', recovered);
	input.removeAttribute('aria-invalid');
}

// --- Per-form setup ----------------------------------------------------------

function setupForm(form) {
	const config = {
		endpoint: form.dataset.endpoint || '',
		action: form.dataset.action || '',
		nonce: form.dataset.nonce || '',
		hpToken: form.dataset.hpToken || '',
		contactEmail: form.dataset.contactEmail || '',
		phoneDisplay: form.dataset.phoneDisplay || '',
		demo: form.dataset.demo === '1',
		formTesting: form.dataset.formTesting === '1',
	};
	const loadedAt = Math.floor(Date.now() / 1000);
	const errored = new WeakSet();

	// We render our own messages — suppress the browser bubbles.
	form.setAttribute('novalidate', '');

	if (config.formTesting) {
		const autofillBtn =
			form.querySelector('[data-form-autofill]') ||
			form.parentElement?.querySelector('[data-form-autofill]');
		autofillBtn?.addEventListener('click', () => autofillForm(form));
	}

	// Phone mask.
	qsa('[data-format="phone-us"]', form).forEach((input) => {
		const applyFormat = () => {
			input.value = formatUSPhone(input.value);
		};
		input.addEventListener('input', applyFormat);
		input.addEventListener('paste', () => requestAnimationFrame(applyFormat));
	});

	const validateField = (input) => {
		const message = fieldMessage(input);
		if (message) {
			errored.add(input);
			showFieldError(input, message);
			return false;
		}
		clearFieldError(input, { recovered: errored.has(input) });
		return true;
	};

	// Live re-validation: only after a field has errored once (Parsley's
	// "eager" feel — quiet until you get it wrong, instant once you have).
	validatableFields(form).forEach((input) => {
		const revalidate = () => {
			if (errored.has(input)) validateField(input);
		};
		input.addEventListener('input', revalidate);
		input.addEventListener('change', revalidate);
		input.addEventListener('blur', revalidate);
	});

	const validateAll = () => {
		const invalid = validatableFields(form).filter((input) => !validateField(input));
		return invalid;
	};

	const scrollToFirstError = () => {
		const first = form.querySelector('.is-invalid');
		if (first) {
			first.scrollIntoView({ behavior: 'smooth', block: 'center' });
			first.focus({ preventScroll: true });
		}
	};

	form.addEventListener('submit', (event) => {
		const invalid = validateAll();

		if (invalid.length) {
			event.preventDefault();
			scrollToFirstError();
			form.dispatchEvent(
				new CustomEvent('form:invalid', { bubbles: true, detail: { fields: invalid.map((i) => i.name) } })
			);
			return;
		}

		// No endpoint and not a demo → native submission (progressive enhancement).
		if (!config.endpoint && !config.demo) return;

		event.preventDefault();
		submitForm(form, config, loadedAt);
	});

	const overlay = successRegion(form);
	if (overlay) {
		bindSuccessDismiss(overlay, form);
	}

	if (hasContactSentCookie()) {
		maybeShowWelcomeBackOverlay(form, config, overlay);
	}
}

// --- Submission flow ---------------------------------------------------------

function wait(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

async function ensureMinDuration(startedAt) {
	const remaining = SUBMIT_MIN_DURATION_MS - (Date.now() - startedAt);
	if (remaining > 0) await wait(remaining);
}

function setSubmitting(form, isSubmitting) {
	const button = form.querySelector('button[type="submit"], input[type="submit"]');

	form.classList.toggle('is-submitting', isSubmitting);
	if (button) {
		button.classList.toggle('is-loading', isSubmitting);
		button.disabled = isSubmitting;
		button.setAttribute('aria-busy', String(isSubmitting));

		const loading =
			button.querySelector('.form-submit-loading, .btn__loading');
		const label =
			button.querySelector('.form-submit-label, .btn__label, .lbl');

		loading?.setAttribute('aria-hidden', isSubmitting ? 'false' : 'true');
		label?.setAttribute('aria-hidden', isSubmitting ? 'true' : 'false');
	}
}

function alertRegion(form) {
	return (
		document.querySelector(`[data-form-alert][data-for="${form.dataset.form}"]`) ||
		form.querySelector('[data-form-alert]')
	);
}

function showFormError(form, message, fallbackMailto) {
	const alert = alertRegion(form);
	if (!alert) return;

	alert.replaceChildren();

	const messageEl = document.createElement('p');
	messageEl.className = 'form-alert__message';
	messageEl.textContent = message;
	alert.appendChild(messageEl);

	if (fallbackMailto?.href) {
		const link = document.createElement('a');
		link.href = fallbackMailto.href;
		link.className = 'btn btn--solid btn--sm form-alert__fallback';
		link.textContent = fallbackMailto.label || 'Open in your email app';
		alert.appendChild(link);
	}

	alert.hidden = false;
	alert.classList.add('is-visible');
	alert.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function clearFormError(form) {
	const alert = alertRegion(form);
	if (!alert) return;
	alert.replaceChildren();
	alert.classList.remove('is-visible');
	alert.hidden = true;
}

const TESTING_SAMPLE = {
	'first-name': 'Jane',
	'last-name': 'Doe',
	phone: '(954) 555-0123',
	email: 'jane@example.com',
	message:
		'Sample test inquiry — shoulder pain after training. Looking to schedule an initial consultation.',
};

function firstSelectOption(select) {
	return [...select.options].find((option) => option.value.trim() !== '');
}

function setFieldValue(input, value) {
	if (input.tagName === 'SELECT') {
		const match = value ? [...input.options].find((option) => option.value === value) : null;
		const option = match || firstSelectOption(input);
		if (!option) return;
		input.value = option.value;
	} else {
		input.value = value;
	}

	input.dispatchEvent(new Event('input', { bubbles: true }));
	input.dispatchEvent(new Event('change', { bubbles: true }));
}

function autofillForm(form) {
	Object.entries(TESTING_SAMPLE).forEach(([name, value]) => {
		const input = form.elements[name];
		if (!input) return;
		setFieldValue(input, value);
	});

	validatableFields(form).forEach((input) => {
		if (input.tagName !== 'SELECT' || input.value.trim() !== '') return;
		setFieldValue(input, '');
	});
}

// Generic mailto fallback built from the form's own fields and labels.
function buildMailtoFallback(form, config) {
	if (!config.contactEmail) return null;

	const lines = [];
	validatableFields(form).forEach((input) => {
		const value = input.value?.trim();
		if (!value) return;
		const label = form.querySelector(`label[for="${input.id}"]`)?.textContent?.trim() || input.name;
		lines.push(`${label.replace(/\s*\*$/, '')}: ${value}`);
	});
	lines.push(`Page URL: ${window.location.href}`);

	const subject = 'New message from website form';
	const body = lines.join('\n');

	return {
		href: `mailto:${config.contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
		label: 'Open in your email app',
	};
}

function handleDeliveryFailure(form, config, message, fallbackMailto) {
	const mailto = fallbackMailto || buildMailtoFallback(form, config);
	const finalMessage =
		message ||
		(mailto
			? "We couldn't deliver your message automatically. Your email app will open so you can send it directly."
			: `We couldn't send your message right now. Please try again${config.phoneDisplay ? ` or call ${config.phoneDisplay}` : ''}.`);

	showFormError(form, finalMessage, mailto);
	if (mailto?.href) window.location.href = mailto.href;

	form.dispatchEvent(new CustomEvent('form:failure', { bubbles: true, detail: { message: finalMessage } }));
}

function applyServerErrors(form, errors) {
	let hasFieldError = false;

	Object.entries(errors).forEach(([name, message]) => {
		const input = form.elements[name];
		if (!input || typeof message !== 'string') return;
		input.classList.remove('is-recovered');
		showFieldError(input, message);
		hasFieldError = true;
	});

	return hasFieldError;
}

function successRegion(form) {
	return (
		document.querySelector(`[data-form-success][data-for="${form.dataset.form}"]`) ||
		form.querySelector('[data-form-success]') ||
		form.parentElement?.querySelector('[data-form-success]')
	);
}

function successCard(overlay) {
	return overlay?.querySelector('.form-success') || overlay;
}

function getCookie(name) {
	const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
	return match ? decodeURIComponent(match[1]) : '';
}

function hasContactSentCookie() {
	return getCookie(CONTACT_SENT_COOKIE) === '1';
}

function setContactSentCookie() {
	const secure = window.location.protocol === 'https:' ? '; Secure' : '';
	document.cookie = `${CONTACT_SENT_COOKIE}=1; Max-Age=${CONTACT_SENT_MAX_AGE_SEC}; Path=/; SameSite=Lax${secure}`;
}

function welcomeBackVariant(overlay) {
	return overlay?.dataset.formSuccessVariant === 'return' ? 'return' : 'submit';
}

function applySuccessCopy(overlay, successData = {}) {
	const slots = {
		eyebrow: '[data-success-eyebrow]',
		title: '[data-success-title]',
		message: '[data-success-message]',
		submessage: '[data-success-submessage]',
	};

	Object.entries(slots).forEach(([key, selector]) => {
		const el = overlay.querySelector(selector);
		if (!el) return;

		if (key === 'submessage') {
			if (successData.submessage) {
				el.textContent = successData.submessage;
				el.hidden = false;
			} else {
				el.textContent = '';
				el.hidden = true;
			}
			return;
		}

		if (key === 'eyebrow') {
			if (successData.eyebrow) {
				el.textContent = successData.eyebrow;
				el.hidden = false;
			}
			return;
		}

		if (successData[key]) {
			el.textContent = successData[key];
		}
	});
}

function revealSuccessOverlay(overlay) {
	const card = successCard(overlay);
	if (!overlay || !card) return;

	overlay.hidden = false;
	overlay.removeAttribute('aria-hidden');
	overlay.setAttribute('role', 'status');
	overlay.setAttribute('aria-live', 'polite');

	requestAnimationFrame(() => {
		card.classList.add('is-visible');
	});
}

function hideSuccessOverlay(overlay) {
	const card = successCard(overlay);
	if (!overlay) return;

	card?.classList.remove('is-visible');
	overlay.hidden = true;
	overlay.setAttribute('aria-hidden', 'true');
	overlay.removeAttribute('role');
	overlay.removeAttribute('aria-live');
}

function bindSuccessDismiss(overlay, form) {
	overlay.querySelectorAll('[data-form-success-dismiss]').forEach((button) => {
		if (button.dataset.boundDismiss === '1') return;

		button.dataset.boundDismiss = '1';
		button.addEventListener('click', () => {
			hideSuccessOverlay(overlay);
			form.dispatchEvent(new CustomEvent('form:success:dismiss', { bubbles: true }));
		});
	});
}

function replaceSuccessOverlay(form, html) {
	const stack = form.parentElement;
	const existing = successRegion(form);
	if (!stack || !html.trim()) return null;

	existing?.remove();
	stack.insertAdjacentHTML('beforeend', html);

	const overlay = successRegion(form);
	if (overlay) {
		bindSuccessDismiss(overlay, form);
	}

	return overlay;
}

function maybeShowWelcomeBackOverlay(form, config, overlay = successRegion(form)) {
	if (!hasContactSentCookie()) return;

	if (overlay && welcomeBackVariant(overlay) === 'return') {
		revealSuccessOverlay(overlay);
		return;
	}

	fetchWelcomeBackOverlay(form, config);
}

async function fetchWelcomeBackOverlay(form, config) {
	if (!config.endpoint || !config.nonce || !hasContactSentCookie()) return;

	const payload = new FormData();
	payload.append('action', SNAPSHOT_ACTION);
	payload.append('_ajax_nonce', config.nonce);

	try {
		const response = await fetch(config.endpoint, {
			method: 'POST',
			body: payload,
			credentials: 'same-origin',
			headers: { Accept: 'application/json' },
		});

		let data = null;
		try {
			data = await response.json();
		} catch {
			return;
		}

		const html = data?.data?.html ?? data?.html ?? '';
		if (!data?.success || !String(html).trim()) return;

		const overlay = replaceSuccessOverlay(form, String(html));
		if (overlay) revealSuccessOverlay(overlay);
	} catch {
		// Silent — form remains usable without the welcome-back overlay.
	}
}

function showSuccessView(form, successData = {}) {
	const overlay = successRegion(form);
	if (!overlay) return;

	setContactSentCookie();
	applySuccessCopy(overlay, successData);
	form.reset();
	revealSuccessOverlay(overlay);
}

function buildPayload(form, config, loadedAt) {
	const payload = new FormData(form);

	payload.append('page_url', window.location.href);
	payload.append('_form_loaded', String(loadedAt));
	if (config.hpToken) payload.append('_hp_token', config.hpToken);
	if (config.action) payload.append('action', config.action);
	if (config.nonce) payload.append('_ajax_nonce', config.nonce);
	if (config.formTesting) payload.append('_debug', '1');

	return payload;
}

async function submitForm(form, config, loadedAt) {
	clearFormError(form);
	setSubmitting(form, true);
	const startedAt = Date.now();

	const succeed = (data) => {
		showSuccessView(form, data?.successData);
		form.dispatchEvent(new CustomEvent('form:success', { bubbles: true, detail: { data } }));
	};

	try {
		if (config.demo) {
			await ensureMinDuration(startedAt);
			succeed({ success: true });
			return;
		}

		const response = await fetch(config.endpoint, {
			method: 'POST',
			body: buildPayload(form, config, loadedAt),
			credentials: 'same-origin',
			headers: { Accept: 'application/json' },
		});

		let data = null;
		try {
			data = await response.json();
		} catch {
			throw new Error('Unexpected server response.');
		}

		if (!response.ok && !data) {
			throw new Error('Something went wrong. Please try again.');
		}

		await ensureMinDuration(startedAt);

		if (data.success) {
			succeed(data);
			return;
		}

		if (data.errors && applyServerErrors(form, data.errors)) {
			const first = form.querySelector('.is-invalid');
			first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			first?.focus({ preventScroll: true });
			showFormError(form, data.message || 'Please correct the errors and try again.');
			return;
		}

		if (data.fallbackMailto) {
			handleDeliveryFailure(form, config, data.message, data.fallbackMailto);
			return;
		}

		showFormError(form, data.message || 'Something went wrong. Please try again or contact us directly.');
		form.dispatchEvent(new CustomEvent('form:failure', { bubbles: true, detail: { message: data.message } }));
	} catch {
		await ensureMinDuration(startedAt);
		handleDeliveryFailure(form, config, null, null);
	} finally {
		setSubmitting(form, false);
	}
}
